/**
 * useShowroomSync Hook
 *
 * Syncs user's preferred showroom between:
 * - Browser cookie (local storage for guests)
 * - SFCC customer profile (for logged-in users)
 *
 * Uses SCAPI Shopper Customers API for updating custom attributes.
 * Requires c_preferredStoreId to be "Externally Visible" in Business Manager.
 *
 * Flow:
 * 1. On login: Load c_preferredStoreId from account → save to cookie
 * 2. On showroom selection: Save to cookie, and if logged in → save to account via SCAPI
 */

import {useCallback, useEffect, useRef, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {
  getMyShowroomFromCookie,
  parseAccountShowroom,
  setMyShowroomToCookie,
  SHOWROOM_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';

/**
 * Hook to sync showroom preference between cookie and SFCC account
 *
 * @returns {Object} { syncShowroomToAccount, isLoading, error }
 */
export const useShowroomSync = () => {
  const {site} = useMultiSite();
  const siteId = site?.id;

  const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer();
  const {isRegistered, customerId} = customer || {};

  // Use SCAPI mutation for updating customer
  const updateCustomerMutation = useShopperCustomersMutation('updateCustomer');
  const queryClient = useQueryClient();

  const [error, setError] = useState(null);
  const syncInProgressRef = useRef(false);
  const lastSyncedShowroomRef = useRef(null);

  // Sync showroom between cookie and account when user logs in
  useEffect(() => {
    if (!siteId || isCustomerLoading || !isRegistered || !customerId) {
      // Reset sync state when user logs out or data is loading
      syncInProgressRef.current = false;
      lastSyncedShowroomRef.current = null;

      return;
    }

    // Prevent duplicate syncs
    if (syncInProgressRef.current) return;

    const cookieShowroom = getMyShowroomFromCookie(siteId);
    const accountShowroom = parseAccountShowroom(customer?.c_preferredStoreId, siteId);

    // Case 1: Account has showroom, cookie doesn't or is different → sync account to cookie
    if (accountShowroom && (!cookieShowroom || cookieShowroom.id !== accountShowroom.id)) {
      // Check if we've already synced this account showroom to cookie
      if (lastSyncedShowroomRef.current === accountShowroom.id) {
        return;
      }

      setMyShowroomToCookie(siteId, accountShowroom);
      lastSyncedShowroomRef.current = accountShowroom.id;
    } else if (cookieShowroom && !accountShowroom) {
      // Case 2: Cookie has showroom, account doesn't → sync cookie to account
      // Check if we've already synced this cookie showroom to account
      if (lastSyncedShowroomRef.current === cookieShowroom.id) {
        return;
      }

      // Mark sync as in progress to prevent duplicate syncs
      syncInProgressRef.current = true;
      lastSyncedShowroomRef.current = cookieShowroom.id;

      // Add siteId to the showroom data for multi-site support
      const showroomData = {
        ...cookieShowroom,
        siteId,
      };

      // Sync cookie showroom to account and wait for completion
      updateCustomerMutation.mutateAsync({
        parameters: {customerId},
        body: {
          c_preferredStoreId: JSON.stringify(showroomData),
        },
      }).then(() => {
        // Invalidate all queries to ensure fresh data on next login
        // This ensures that when user logs in again, the customer query will refetch
        // and include the updated c_preferredStoreId attribute
        // Using invalidateQueries() without params invalidates all queries (similar to shopper context)
        queryClient.invalidateQueries();
      }).catch((err) => {
        console.error('[ShowroomSync] Failed to sync cookie showroom to account:', err);
        setError(err);
        // Reset sync state on error so it can retry
        syncInProgressRef.current = false;
        lastSyncedShowroomRef.current = null;
      }).finally(() => {
        // Reset sync in progress after a delay to allow for query invalidation and refetch
        // Using a shorter delay as query invalidation is typically fast
        setTimeout(() => {
          syncInProgressRef.current = false;
        }, 1000);
      });
    }
  }, [siteId, isRegistered, customerId, customer?.c_preferredStoreId, isCustomerLoading, updateCustomerMutation, queryClient]);

  // Function to sync showroom to account using SCAPI
  const syncShowroomToAccount = useCallback(
    async (showroom) => {
      if (!isRegistered || !customerId || !showroom) {
        return false;
      }

      setError(null);

      try {
        // Add siteId to the showroom data for multi-site support
        const showroomData = {
          ...showroom,
          siteId,
        };

        // Use SCAPI to update customer with custom attribute
        await updateCustomerMutation.mutateAsync({
          parameters: {customerId},
          body: {
            // Custom attributes are prefixed with c_
            c_preferredStoreId: JSON.stringify(showroomData),
          },
        });

        // Invalidate queries to ensure fresh data
        queryClient.invalidateQueries();

        return true;
      } catch (err) {
        setError(err);

        return false;
      }
    },
    [isRegistered, customerId, siteId, updateCustomerMutation, queryClient]
  );

  // Listen for showroom changes and auto-sync to account
  useEffect(() => {
    if (!isRegistered || !customerId) return;

    const handleShowroomChanged = async (event) => {
      const showroom = event.detail?.showroom;

      if (showroom) {
        await syncShowroomToAccount(showroom);
      }
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [isRegistered, customerId, syncShowroomToAccount]);

  return {
    syncShowroomToAccount,
    isLoading: updateCustomerMutation.isPending,
    error,
    isRegistered,
  };
};

export default useShowroomSync;
